import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import CardReporting, { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { getTemplateSummaryQuery, useCustomersEngagementQuery } from "./queries";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import styles from "./summarySection.module.scss";

import "features/Library/Common/utils/performanceSCSSUtils.scss";

export interface Props {
  templateId: number;
  filter: PerformanceWithAccountFilter;
}

export const SummarySection = ({ templateId, filter }: Props) => {
  const summaryRequest = useQuery({
    queryKey: ["templateSummary", templateId, filter],
    queryFn: getTemplateSummaryQuery,
    enabled: filter.type !== undefined,
  });

  const customerRequest = useCustomersEngagementQuery(templateId, filter);

  const cards = useMemo<CardReportingItem[]>(() => {
    if (!summaryRequest.data || !customerRequest.data) {
      return [];
    }

    return [
      {
        statDescription: "Enrolled Customers",
        stat: customerRequest.data.Enrollments,
        popupBody: "Total customers enrolled in this template during the specified time period.",
      },
      {
        statDescription: "Enrolled Users",
        stat: summaryRequest.data.Users,
        popupBody: "Total users enrolled in this template during the specified time period.",
      },
      {
        statDescription: "Packs",
        stat: summaryRequest.data.Packs,
        popupBody: "Total Packs included with this template during the specified time period.",
      },
      {
        statDescription: "Flows",
        stat: summaryRequest.data.Flows,
        popupBody: "Total Flows included with this template during the specified time period.",
      },
    ];
  }, [customerRequest.data, summaryRequest.data]);

  return (
    <div className={styles.cards}>
      <RequestStatusRenderer state={summaryRequest.status}>
        {summaryRequest.data && <CardReporting items={cards} />}
      </RequestStatusRenderer>
    </div>
  );
};
